import React from 'react';

import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

export type OpenTab = {
  fileId: string;
  name: string;
  /** 未保存の変更があるか */
  dirty: boolean;
  /** 固定タブ（閉じる不可・常に先頭）。ガイドタブなど */
  pinned?: boolean;
  /** タブ左に表示するアイコン */
  icon?: React.ReactNode;
  /** 絶対パス（ホバーでツールチップ表示。ガイドタブなど無い場合は未指定）（API6106 の path） */
  path?: string;
};

type Props = {
  tabs: OpenTab[];
  activeFileId: string | null;
  onSelect: (fileId: string) => void;
  onClose: (fileId: string) => void;
};

/**
 * 開いているファイルのタブバー（右ペイン上部）。
 */
const TabBar: React.FC<Props> = ({ tabs, activeFileId, onSelect, onClose }) => {
  if (tabs.length === 0) return null;

  return (
    <Stack
      direction="row"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        overflowX: 'auto',
        flexShrink: 0,
        bgcolor: 'action.hover',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.fileId === activeFileId;
        return (
          <Tooltip key={tab.fileId} title={tab.path ?? ''} disableHoverListener={!tab.path} arrow>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => onSelect(tab.fileId)}
              sx={{
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: isActive ? 'background.paper' : 'transparent',
                borderBottom: isActive ? 2 : 0,
                borderBottomColor: 'primary.main',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: isActive ? 'background.paper' : 'action.selected' },
              }}
            >
              {tab.icon && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  {tab.icon}
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{ color: isActive ? 'text.primary' : 'text.secondary' }}
              >
                {tab.name}
              </Typography>
              {tab.pinned ? null : tab.dirty ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(tab.fileId);
                  }}
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    '&:hover .close-icon': { display: 'block' },
                    '&:hover .dirty-icon': { display: 'none' },
                  }}
                >
                  <CircleIcon
                    className="dirty-icon"
                    sx={{ fontSize: 10, color: 'text.secondary' }}
                  />
                  <CloseIcon
                    className="close-icon"
                    sx={{ fontSize: 14, display: 'none', color: 'text.secondary' }}
                  />
                </Box>
              ) : (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(tab.fileId);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default TabBar;
